import type {
  GeneratedImageResult,
  ImageGenParams,
  ImageProvider,
  ImageProviderConfig,
} from "@roqeet/core";
import { base64ToBlob } from "./openai-image.js";

/**
 * Google Gemini 이미지 생성 어댑터 — 결정 #19. "nano-banana"(Gemini 이미지)는
 * inline base64로 결과를 돌려준다(`generateContent`의 inlineData). 키는 쿼리
 * 파라미터로 전달한다(텍스트 Gemini 어댑터와 동일 규약, 결정 #7).
 *
 * 모델 예: "gemini-2.0-flash-preview-image-generation".
 */
export class GeminiImageProvider implements ImageProvider {
  readonly kind = "gemini";
  readonly supportsProgress = false;

  async generate(
    params: ImageGenParams,
    config: ImageProviderConfig,
    _onProgress?: (pct: number, previewBlob?: Blob) => void,
  ): Promise<GeneratedImageResult[]> {
    const base = (config.endpoint ?? "https://generativelanguage.googleapis.com/v1beta").replace(
      /\/+$/,
      "",
    );
    const model = config.defaultModel ?? "gemini-2.0-flash-preview-image-generation";

    // 키는 액션이 extraHeaders["x-goog-api-key"]로 주입(볼트에서 해석).
    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(config.extraHeaders ?? {}),
    };

    const body = {
      contents: [{ role: "user", parts: [{ text: params.prompt }] }],
      generationConfig: { responseModalities: ["IMAGE", "TEXT"] },
    };

    const init: RequestInit = { method: "POST", headers, body: JSON.stringify(body) };
    if (params.signal) init.signal = params.signal;

    const res = await fetch(`${base}/models/${encodeURIComponent(model)}:generateContent`, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Gemini 이미지 생성 실패 (HTTP ${res.status}): ${text.slice(0, 400)}`);
    }
    const json = (await res.json()) as GeminiImageResponse;
    const parts = json.candidates?.[0]?.content?.parts ?? [];
    const results: GeneratedImageResult[] = [];

    for (const part of parts) {
      const inline = part.inlineData ?? part.inline_data;
      if (inline?.data) {
        const mime = inline.mimeType ?? inline.mime_type ?? "image/png";
        results.push({
          blob: base64ToBlob(inline.data, mime),
          provenance: {
            provider: config.providerId ?? this.kind,
            model,
            prompt: params.prompt,
            ...(params.negativePrompt ? { negativePrompt: params.negativePrompt } : {}),
          },
        });
      }
    }
    if (results.length === 0) {
      throw new Error("Gemini 응답에 이미지가 없습니다(안전 필터 또는 모델 미지원일 수 있음).");
    }
    return results;
  }
}

interface GeminiImageResponse {
  candidates?: {
    content?: {
      parts?: {
        inlineData?: { data?: string; mimeType?: string; mime_type?: string };
        inline_data?: { data?: string; mimeType?: string; mime_type?: string };
      }[];
    };
  }[];
}
